import { hasElements } from "utils/arrays";
import { clientFieldsConfig } from "components/Settings/Forms/Forms/utils";

export const formatDate = (value) => {
    const dateObj = value && (value instanceof Date || typeof value === "number") ? new Date(value) : "";
    return dateObj ? new Intl.DateTimeFormat("en-US", { year: "numeric", month: "2-digit", day: "2-digit" }).format(dateObj) : "";
};

/** Long form used by the addendum header, e.g. "August 3, 2026". */
export const formatLongDate = (value) => {
    const dateObj = value ? new Date(value) : "";
    return dateObj && !isNaN(dateObj)
        ? new Intl.DateTimeFormat("en-US", { year: "numeric", month: "long", day: "numeric" }).format(dateObj)
        : "";
};

export const getFormattedFieldValue = (mapping, clientValue) => {
    let value = clientValue;

    if (mapping.type === "checkbox") {
        return (value || "").toLowerCase() === "true";
    } else {
        if (String(mapping.type).startsWith("string_date")) {
            return formatDate(value);
        }
        return String(value || "").replaceAll("-", "");
    }
};

export const getFormattedValue = (type, value) => {
    if (type === "checkbox") {
        return (value || "").toLowerCase() === "true";
    }
    return value;
};

export const buildFieldTriggers = (formFieldMappings) => {
    if (!hasElements(formFieldMappings)) return {};

    const triggers = {};
    formFieldMappings.forEach((mapping) => {
        if (mapping.clientField?.startsWith("@formFieldValue.")) {
            const dependentFieldName = mapping.clientField.replace("@formFieldValue.", "");
            triggers[dependentFieldName] = mapping.pdfField;
        }
    });
    return triggers;
};

export const resolveFieldValue = (mapping, formFieldMappings, preloadersData) => {
    const config = clientFieldsConfig.find((x) => x.id === mapping.clientField);
    if (config) {
        let fieldValue = "";
        if (config.origin === "Client") {
            fieldValue = preloadersData.client[mapping.clientField];
        } else if (config.origin === "System") {
            fieldValue = preloadersData.system[mapping.clientField];
        } else if (config.origin === "Attorney") {
            // We split the string to get the key of the attorney user object
            const fieldKey = String(mapping.clientField).split(".")[1];
            fieldValue = preloadersData.attorneyUser?.[fieldKey];
        } else if (config.origin === "DocIssuer") {
            const fieldKey = String(mapping.clientField).split(".")[1];
            fieldValue = preloadersData.docIssuer?.[fieldKey];
        }
        return getFormattedFieldValue(mapping, fieldValue);
    } else {
        if (mapping.clientField?.startsWith("@formFieldValue.")) {
            const dependentFieldName = mapping.clientField.replace("@formFieldValue.", "");
            const dependentFieldMapping = formFieldMappings.find((m) => m.pdfField === dependentFieldName);
            if (dependentFieldMapping) {
                return resolveFieldValue(dependentFieldMapping, formFieldMappings, preloadersData);
            }
        }
    }
    return "";
};

export const buildPreloadedValues = (formFieldMappings, preloadersData) => {
    if (!preloadersData.client || !hasElements(formFieldMappings)) return {};

    return formFieldMappings
        .filter((m) => Boolean(m.clientField))
        .reduce((obj, mapping) => {
            const fieldValue = resolveFieldValue(mapping, formFieldMappings, preloadersData);
            if (fieldValue) {
                return {
                    ...obj,
                    [mapping.pdfField]: fieldValue,
                };
            }
            return obj;
        }, {});
};

/** "immigration" -> "Immigration" */
export const getModuleLabel = (module) => (module ? module.charAt(0).toUpperCase() + module.slice(1) : "");

/** Addendums as the API expects them: client-only keys stripped, blanks as null. */
export const toAddendumsPayload = (addendums = []) =>
    addendums.map(({ id, reference, pageNumber, text }) => ({
        id: id ?? null,
        reference: reference || null,
        pageNumber: pageNumber ?? null,
        text,
    }));

/** Where the PDF of a form version is stored. */
export const getFormPdfLocation = (form) => ({
    dirName: `settings/forms/pdf-files/${form.issuer.id}/${form.category.id}`,
    fileName: `${form.code}_${form.version}.pdf`,
});
